// T12 (LAUNCH_PLAN.md) — Live preflight consent schema + builder.
//
// Every host MUST attest to 4 material facts before going live, on every
// session. The payload is persisted on `liveRooms/{id}.preflightConsent`
// (runtime room) and `events/{id}.preflightConsent` (scheduled event), and
// the Firestore rule on `create` enforces all four items === true.
//
// Single source of truth — bump PREFLIGHT_CONSENT_VERSION here and the
// modal copy text in `LivePreflightModal.tsx` together. The Firestore rule
// uses `is string` (not a pinned literal), so old rooms retain their
// historical version in the immutable audit log.

import { serverTimestamp, type FieldValue, type Timestamp } from 'firebase/firestore';

export const PREFLIGHT_CONSENT_VERSION = 'live-preflight-2026-05';

export type PreflightSurface =
  | 'live'
  | 'audio'
  | 'podcast'
  | 'gaming'
  | 'meeting'
  | 'battle'
  | 'shorts'
  | 'event';

export interface PreflightItems {
  recordingAware: true;
  publicationAware: true;
  communityGuidelines: true;
  ageEighteenPlus: true;
}

// Shape persisted on Firestore. `acceptedAt` is a FieldValue at write-time
// (serverTimestamp sentinel) and a Timestamp on read-back.
export interface PreflightConsent {
  version: typeof PREFLIGHT_CONSENT_VERSION;
  acceptedAt: FieldValue | Timestamp;
  acceptedFromSurface: PreflightSurface;
  hostId: string;
  items: PreflightItems;
  userAgentHash: string;
}

// Hex sha-256 of `input`, truncated to 16 chars. Used to fingerprint the
// host's user-agent for the audit log without storing the raw string.
export async function hashUserAgent(input: string): Promise<string> {
  if (!input) return '';
  try {
    const enc = new TextEncoder().encode(input);
    const digest = await crypto.subtle.digest('SHA-256', enc);
    const bytes = new Uint8Array(digest);
    let hex = '';
    for (let i = 0; i < bytes.length; i++) {
      hex += bytes[i].toString(16).padStart(2, '0');
    }
    return hex.slice(0, 16);
  } catch {
    // Web Crypto unavailable (very old browser / SSR). Fall back to a
    // deterministic non-cryptographic tag so the audit row is not blank.
    let h = 0;
    for (let i = 0; i < input.length; i++) {
      h = (h * 31 + input.charCodeAt(i)) | 0;
    }
    return `nc_${(h >>> 0).toString(16).padStart(8, '0')}`;
  }
}

export interface BuildPreflightConsentArgs {
  surface: PreflightSurface;
  hostId: string;
  userAgent?: string;
}

// Build the consent payload a caller passes into createLiveRoom / createEvent.
// All four items are literal true — the caller proved that via the modal.
// acceptedAt is a serverTimestamp sentinel; Firestore stamps it on write.
export async function buildPreflightConsent({
  surface,
  hostId,
  userAgent,
}: BuildPreflightConsentArgs): Promise<PreflightConsent> {
  const ua = userAgent ?? (typeof navigator !== 'undefined' ? navigator.userAgent : '');
  const userAgentHash = await hashUserAgent(ua);
  return {
    version: PREFLIGHT_CONSENT_VERSION,
    acceptedAt: serverTimestamp(),
    acceptedFromSurface: surface,
    hostId,
    items: {
      recordingAware: true,
      publicationAware: true,
      communityGuidelines: true,
      ageEighteenPlus: true,
    },
    userAgentHash,
  };
}

// Runtime guard used by createLiveRoom / createEvent before they accept the
// payload. Fails loud (throws) so non-UI callers cannot silently bypass T12.
export function assertValidPreflightConsent(
  c: unknown,
  expectedHostId: string,
): asserts c is PreflightConsent {
  if (!c || typeof c !== 'object') {
    throw new Error('[T12] preflightConsent missing — host must complete the Go Live modal');
  }
  const cc = c as Partial<PreflightConsent>;
  if (typeof cc.version !== 'string' || !cc.version.startsWith('live-preflight-')) {
    throw new Error('[T12] preflightConsent.version invalid');
  }
  if (cc.hostId !== expectedHostId) {
    throw new Error('[T12] preflightConsent.hostId mismatch with auth.uid');
  }
  if (!cc.acceptedAt) {
    throw new Error('[T12] preflightConsent.acceptedAt missing');
  }
  const items = cc.items as Partial<PreflightItems> | undefined;
  if (
    !items ||
    items.recordingAware !== true ||
    items.publicationAware !== true ||
    items.communityGuidelines !== true ||
    items.ageEighteenPlus !== true
  ) {
    throw new Error('[T12] preflightConsent.items — all four attestations required');
  }
}
